import React from 'react';
import { DateRangeValue } from '@/types/asinSalesHistory';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { cn } from '@/lib/utils';

interface DateRangeSelectorProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ value, onChange }) => {
  const presetOptions = [
    { label: 'Last 7 days', value: 'last_7_days' as const, days: 7 },
    { label: 'Last 30 days', value: 'last_30_days' as const, days: 30 },
    { label: 'Last 90 days', value: 'last_90_days' as const, days: 90 },
    { label: 'Custom', value: 'custom' as const, days: null },
  ];

  const handlePresetChange = (preset: DateRangeValue['preset']) => {
    if (preset === 'custom') {
      onChange({
        preset,
        startDate: value.startDate || subDays(new Date(), 30),
        endDate: value.endDate || new Date()
      });
    } else {
      const option = presetOptions.find(opt => opt.value === preset);
      if (option && option.days) {
        onChange({
          preset,
          startDate: subDays(new Date(), option.days),
          endDate: new Date()
        });
      }
    }
  };

  const formatDateRange = () => {
    if (!value.startDate || !value.endDate) return 'Select date range';
    
    const start = format(value.startDate, 'MMM d, yyyy');
    const end = format(value.endDate, 'MMM d, yyyy');
    return `${start} - ${end}`;
  };

  return (
    <div className="flex items-center space-x-2">
      {/* Preset Buttons */}
      <div className="flex items-center space-x-1">
        {presetOptions.slice(0, -1).map((option) => (
          <Button
            key={option.value}
            variant={value.preset === option.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePresetChange(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {/* Custom Date Range Picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={value.preset === 'custom' ? 'default' : 'outline'}
            size="sm"
            className={cn(
              "justify-start text-left font-normal",
              !value.startDate && !value.endDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value.preset === 'custom' ? formatDateRange() : 'Custom'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Card>
            <CardContent className="p-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-sm font-medium mb-2">Start Date</p>
                  <Calendar
                    mode="single"
                    selected={value.startDate || undefined}
                    onSelect={(date) => {
                      onChange({
                        preset: 'custom',
                        startDate: date || new Date(),
                        endDate: value.endDate || new Date()
                      });
                    }}
                    initialFocus
                  />
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">End Date</p>
                  <Calendar
                    mode="single"
                    selected={value.endDate || undefined}
                    onSelect={(date) => {
                      onChange({
                        preset: 'custom',
                        startDate: value.startDate || new Date(),
                        endDate: date || new Date()
                      });
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </PopoverContent>
      </Popover>
    </div>
  );
};